/**
 * Arreglo 1.7 (oct-2026) — Crear / editar cliente: datos detallados, descuento, consentimientos.
 * Privado: solo lo ve el barbero dueño (y su barbería en lectura).
 */
import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { clientsApi } from '@/lib/clientsApi';
import { tr } from '@/contexts/LanguageContext';
import { DuplicateAskDialog, isNameDuplicateError } from './DuplicateAskDialog';

const EMPTY = { name: '', last_name: '', phone: '', email: '', birthday: '', address: '', hashtags: '', favorite_cut: '', allergies: '', notes: '', discount_pct: '' };
const CONSENTS = [['marketing_email', 'Acepta correos promocionales'], ['marketing_sms', tr("Acepta mensajes de texto promocionales")], ['photos_public', tr("Autoriza usar sus fotos públicamente")]];

const Field = ({ label, children }) => (
  <label className="block"><span className="text-[11px] font-semibold text-muted-foreground uppercase">{label}</span><div className="mt-1">{children}</div></label>
);

export const ClientEditSheet = ({ open, onOpenChange, mode = 'create', client, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [consents, setConsents] = useState({});
  const [busy, setBusy] = useState(false);
  const [dupAsk, setDupAsk] = useState(null); // 21-oct-2026: {duplicates, body}

  useEffect(() => {
    if (!open) return;
    const c = client || {};
    setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, c[k] ?? ''])), phone: c.phone_display || c.phone || '' });
    setConsents(c.consents || {});
  }, [open, client]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (decision = {}) => {
    if (mode !== 'edit' && !form.phone.trim() && !form.email.trim()) { toast.error(tr("Indica teléfono o correo del cliente (al menos uno)")); return; }
    if (!form.name.trim() && !form.phone.trim() && !form.email.trim()) { toast.error(tr("Indica al menos nombre, teléfono o correo")); return; }
    setBusy(true);
    try {
      const body = { ...form, discount_pct: form.discount_pct === '' ? 0 : Number(form.discount_pct), consents, ...decision };
      Object.keys(body).forEach((k) => { if (body[k] === '') body[k] = null; });
      if (mode === 'edit' && client?.id) await clientsApi.update(client.id, body);
      else {
        const r = await clientsApi.create(body);
        if (r?.merged) toast.success(tr("Contacto añadido a la ficha de {0}", r.client?.display || r.client?.name || ''));
      }
      if (!(mode !== 'edit' && decision.merge_into)) toast.success(mode === 'edit' ? tr("Cliente actualizado") : tr("Cliente creado"));
      setDupAsk(null);
      onSaved?.();
    } catch (e) {
      if (isNameDuplicateError(e)) { setDupAsk({ duplicates: e.payload.detail.duplicates || [] }); return; }
      toast.error(e?.payload?.detail?.message || (typeof e?.payload?.detail === 'string' ? e.payload.detail : null) || e.message || tr("No pudimos guardar"));
    } finally { setBusy(false); }
  };

  return (
    <>
    <DuplicateAskDialog open={Boolean(dupAsk)} duplicates={dupAsk?.duplicates || []} busy={busy}
      onSame={(c) => save({ merge_into: c.id })} onDifferent={() => save({ force_new: true })} onCancel={() => setDupAsk(null)} />
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" aria-describedby={undefined} className="bg-card border-border rounded-t-2xl max-h-[92vh] overflow-y-auto " data-testid="client-edit-sheet">
        <SheetHeader className="text-left"><SheetTitle className="font-display text-lg">{mode === 'edit' ? tr("Editar cliente") : tr("Nuevo cliente")}</SheetTitle></SheetHeader>
        <div className="mt-3 space-y-3">
          {mode !== 'edit' && <p className="text-[11px] text-muted-foreground" data-testid="client-form-contact-hint">{tr("* Teléfono o correo: al menos uno es obligatorio para guardar la ficha.")}</p>}
          <div className="grid grid-cols-2 gap-2">
            <Field label={tr("Nombre")}><Input value={form.name} onChange={set('name')} className="bg-background" data-testid="client-form-name" /></Field>
            <Field label={tr("Apellido")}><Input value={form.last_name} onChange={set('last_name')} className="bg-background" data-testid="client-form-last-name" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tr("Teléfono") + (mode !== 'edit' ? ' *' : '')}><Input value={form.phone} onChange={set('phone')} inputMode="tel" className="bg-background" data-testid="client-form-phone" /></Field>
            <Field label={tr("Correo") + (mode !== 'edit' ? ' *' : '')}><Input value={form.email} onChange={set('email')} inputMode="email" className="bg-background" data-testid="client-form-email" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label={tr("Cumpleaños")}><Input type="date" value={form.birthday} onChange={set('birthday')} className="bg-background" data-testid="client-form-birthday" /></Field>
            <Field label={tr("Descuento (%)")}><Input value={form.discount_pct} onChange={set('discount_pct')} inputMode="decimal" placeholder="0" className="bg-background" data-testid="client-form-discount" /></Field>
          </div>
          <Field label={tr("Dirección")}><Input value={form.address} onChange={set('address')} className="bg-background" data-testid="client-form-address" /></Field>
          <Field label={tr("Etiquetas (#vip #semanal)")}><Input value={form.hashtags} onChange={set('hashtags')} className="bg-background" data-testid="client-form-hashtags" /></Field>
          <Field label={tr("Corte favorito")}><Input value={form.favorite_cut} onChange={set('favorite_cut')} className="bg-background" data-testid="client-form-favorite-cut" /></Field>
          <Field label={tr("Alergias / cuidado especial")}><Input value={form.allergies} onChange={set('allergies')} className="bg-background" data-testid="client-form-allergies" /></Field>
          <Field label={tr("Notas privadas")}><Textarea value={form.notes} onChange={set('notes')} className="bg-background min-h-[70px]" maxLength={1000} data-testid="client-form-notes" /></Field>
          <div className="space-y-2" data-testid="client-form-consents">
            <p className="text-[11px] font-semibold text-muted-foreground uppercase">{tr("Consentimientos")}</p>
            {CONSENTS.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between gap-3 bg-background border border-border rounded-xl px-3 py-2">
                <span className="text-xs">{label}</span>
                <Switch checked={Boolean(consents[k])} onCheckedChange={(v) => setConsents((c) => ({ ...c, [k]: v }))} data-testid={`client-form-consent-${k}`} />
              </div>
            ))}
          </div>
          <Button onClick={() => save()} disabled={busy} className="w-full h-11 rounded-full bg-primary text-primary-foreground font-bold" data-testid="client-form-save">{busy ? tr("Guardando…") : tr("Guardar")}</Button>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
};

export default ClientEditSheet;
